import type { RootState } from '@reducers/index';
import { updateItems } from '@reducers/items';
import type { SearchState } from '@reducers/search';
import { mapSearchToInternalURL, mapSearchToPublicURL } from '@tools/search';
import Router from 'next/router';
import React from 'react';
import { connect } from 'react-redux';
import type { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { wasFirstSearchIdSet } from './search-state-comparer';
import type { FiltersConfigState } from '@reducers/filtersConfig';

interface LSRDispatchProps {
  refreshItems: () => void;
}

interface LSRStateProps {
  search: SearchState;
  isPopstatePrevented: boolean;
  filtersConfigState: FiltersConfigState;
}

type LSRProps = LSRDispatchProps & LSRStateProps;

class ListingShallowRouter extends React.Component<LSRProps> {
  currentUrl = '';
  currentInDialogUrl = '';
  popstateWasPrevented = false;
  geoSearchJustClosed = false;

  componentDidMount() {
    /**
     * This represents the current shown URL in the listing.
     * This is tracked in the pushOrRefresh function and it
     * is used to reconcile the redux status with the browser URL
     */
    this.currentUrl = Router.asPath;

    /**
     * This is used within the filters dialog. In there, we update the listing results
     * in the background to show the number results at the bottom but we do not update the url.
     * We only update the url when leaving the dialog
     */
    this.currentInDialogUrl = Router.asPath;

    Router.beforePopState(() => {
      // This handling is probably useless, but closing the filter dialog on a back could be nice.

      // We need to skip popstate handling if the system reduces says so
      if (this.popstateWasPrevented || this.props.isPopstatePrevented) {
        // We must ensure that this is executed only once and that we update on
        // next tick to avoid conflicts with this event's handling
        this.pushOrRefresh(true);
        return false;
      }

      return true;
    });
  }

  componentDidUpdate(prevProps: LSRProps) {
    this.popstateWasPrevented = prevProps.isPopstatePrevented;
    // We need to track the changes to the geo to refresh the results when
    // cancelling Radius Searches.
    this.geoSearchJustClosed =
      !this.props.search.radiusDialogOpen &&
      prevProps.search.radiusDialogOpen !== this.props.search.radiusDialogOpen;

    // On the first load of the listing (when the page has been sent from the
    // server) the search ID is null in memory, and it is immediately set by
    // the SearchData bridge. We do not want to refresh the listing in this
    // case.
    if (wasFirstSearchIdSet(prevProps.search, this.props.search)) {
      return;
    }

    this.pushOrRefresh();
  }

  private pushOrRefresh(skipPopstateCheck = false) {
    const { search, refreshItems, isPopstatePrevented, filtersConfigState } =
      this.props;
    const { url } = mapSearchToPublicURL(search, filtersConfigState);
    const internalUrl = mapSearchToInternalURL(search, filtersConfigState);

    if (skipPopstateCheck) {
      this.popstateWasPrevented = false;
    }

    if (!isPopstatePrevented || skipPopstateCheck) {
      if (__BROWSER__ && !search.isUpdating && url !== this.currentUrl) {
        // If the URL we built starting from the state is different
        // from the current one, we push the state url

        this.currentUrl = url;
        this.currentInDialogUrl = url;
        // This logic updates the URL, so we don't want the geo override
        this.geoSearchJustClosed = false;

        Router.push(internalUrl, url, { scroll: false, shallow: false });
      }
      // Cancelling geo searches leaves the URL and state in sync
      // But with changed items, so we need to refresh them.
      else if (
        __BROWSER__ &&
        !search.isUpdating &&
        url === this.currentUrl &&
        this.geoSearchJustClosed
      ) {
        refreshItems();
        // After a refresh we don't use this override until
        // there is another state change
        this.geoSearchJustClosed = false;
      }
    } else if (
      (search.filtersDialogOpen || search.radiusDialogOpen) &&
      url !== this.currentInDialogUrl
    ) {
      // If we have the filters dialog opened and the filters have changed
      // we refresh the items to show the updated number of ads at the bottom
      refreshItems();
      this.currentInDialogUrl = url;
    }
  }

  shouldComponentUpdate(nextProps: LSRProps) {
    return !nextProps.search.isUpdating && !this.props.search.isUpdating;
  }

  render() {
    // Maybe this could become an effect
    return null;
  }
}

export default connect<LSRStateProps, LSRDispatchProps, unknown, RootState>(
  (state) => ({
    search: state.search,
    isPopstatePrevented: state.system.preventPopstate,
    filtersConfigState: state.filtersConfig,
  }),
  (dispatch: ThunkDispatch<RootState, void, Action>) => ({
    refreshItems: () => {
      // This updates the items when there is no URL change to trigger it
      dispatch(updateItems());
    },
  })
)(ListingShallowRouter);
